import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Stars, useQuery } from '../../shared/components/Common';
import { SmartImg } from '../../shared/components/Layout';
import Icon from '../../shared/components/Icons';
import { FlowBand } from '../../shared/booking/FlowParts';
import { fmtDay, formatPrice } from '../../shared/data/common';
import { getHotelDetails, reviewHotel } from '../../api/hotels';
import { hotelQueryString, nightsLabel, parseHotelQuery, roomsLabel } from './searchModel';
import { freeCancellationText, optionTypeLabel } from './data';
import { placeLine } from './HotelList';

const roomKey = (option) => option.rooms.map((r) => r.name).join(' + ');

/** "Vainguinim Beach, Panaji, Goa, India 403004". */
export const fullAddress = (a) => (a ? [a.line, a.locality, a.city, a.state, a.country].filter((x, i, all) => x && all.indexOf(x) === i).join(', ') + (a.postalCode ? ` ${a.postalCode}` : '') : '');

function RoomFacts({ details }) {
  if (!details) return null;
  return (
    <ul className="room-facts">
      {details.beds && <li><Icon name="hotels" size={13} /> {details.beds}</li>}
      {details.areaSquareFeet && <li><Icon name="image" size={13} /> {details.areaSquareFeet} sq.ft{details.views.length ? ` · ${details.views.join(', ')}` : ''}</li>}
      {details.maxOccupancy && <li><Icon name="user" size={13} /> Sleeps up to {details.maxOccupancy}</li>}
    </ul>
  );
}

function OptionRow({ option, nights, busy, error, onSelect }) {
  const free = freeCancellationText(option.cancellation);
  const mixed = optionTypeLabel[option.type];
  const [notesOpen, setNotesOpen] = useState(false);
  return (
    <div className="plan-row">
      <div>
        <b>{option.mealBasis || 'Room only'}</b>
        {mixed && <span className="tag">{mixed}</span>}
        <ul>
          {option.inclusions.map((x) => <li key={x}><Icon name="check" size={12} strokeWidth={2.5} /> {x}</li>)}
          {(option.compliance.panRequired || option.compliance.passportRequired) && (
            <li className="muted"><Icon name="file" size={12} /> {option.compliance.panRequired ? 'PAN needed at booking' : 'Passport number needed for every guest'}</li>
          )}
        </ul>
        {free ? <span className="text-primary small">{free}</span> : <span className="text-coral small">Non-refundable</span>}
        {option.bookingNotes && (
          <>
            <button type="button" className="link small" onClick={() => setNotesOpen(!notesOpen)} aria-expanded={notesOpen}>{notesOpen ? 'Hide' : 'Rate'} notes</button>
            {notesOpen && <p className="muted small pre-line">{option.bookingNotes}</p>}
          </>
        )}
        {error && <p className="text-coral small" role="alert">{error}</p>}
      </div>
      <div className="plan-row-price">
        {option.price.strikethrough > option.price.roomPrice && <s className="muted small">{formatPrice(option.price.strikethrough + option.price.convenienceFee)}</s>}
        <b className="price">{formatPrice(option.price.total)}</b>
        <span className="muted small">{nightsLabel(nights)}, incl. taxes & fees</span>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={onSelect}>{busy ? 'Checking…' : 'Select'}</button>
      </div>
    </div>
  );
}

export default function HotelDetails() {
  const q = useQuery();
  const { state } = useLocation();
  const navigate = useNavigate();
  const search = parseHotelQuery(q);
  const resultsPath = `/hotels/results?${hotelQueryString(search)}`;
  const [details, setDetails] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [selecting, setSelecting] = useState(null);
  const [selectError, setSelectError] = useState({});
  const { search: searchId, supplier, hotel: hotelId } = q;

  useEffect(() => {
    if (!searchId || !supplier || !hotelId) return undefined;
    const controller = new AbortController();
    setError('');
    getHotelDetails({ searchId, supplierCode: supplier, hotelId }, controller.signal)
      .then(setDetails)
      .catch((e) => { if (e.name !== 'AbortError') setError(e.message || 'We could not load this hotel.'); });
    return () => controller.abort();
  }, [searchId, supplier, hotelId, attempt]);

  const groups = useMemo(() => {
    const map = new Map();
    (details?.options || []).forEach((o) => {
      const key = roomKey(o);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    });
    return [...map.entries()];
  }, [details]);

  if (!searchId || !supplier || !hotelId) {
    return (
      <div className="container section empty">
        <h2>Hotel not found</h2>
        <Link to="/hotels" className="btn btn-primary">Search hotels</Link>
      </div>
    );
  }

  // Until details load, show what the results page knew.
  const listed = state?.hotel;
  const hotel = details?.hotel;
  const name = hotel?.name || listed?.name || 'Hotel';
  const address = hotel?.address || listed?.address;
  const images = hotel?.images?.length ? hotel.images : [listed?.imageUrl].filter(Boolean);
  const nights = details?.nights || Math.max(1, Math.round((new Date(search.checkout) - new Date(search.checkin)) / 864e5));
  const cheapest = details?.options?.[0] || null;
  const expired = /expired/i.test(error);
  const here = `/hotels/hotel?${new URLSearchParams(q).toString()}`;

  const select = async (option) => {
    setSelecting(option.optionId);
    setSelectError({});
    try {
      const review = await reviewHotel(option.optionId);
      navigate('/hotels/review', { state: { review, backTo: here } });
    } catch (e) {
      setSelectError({ [option.optionId]: e.message || 'This room could not be confirmed. Try another room.' });
      setSelecting(null);
    }
  };

  const lat = address?.latitude;
  const lng = address?.longitude;
  const bbox = lat != null && lng != null ? `${lng - 0.02},${lat - 0.012},${lng + 0.02},${lat + 0.012}` : null;

  return (
    <>
      <FlowBand title={name} step={1} back={{ to: resultsPath, label: 'Back to hotels' }} />
      <div className="container">
        {images.length > 0 && (
          <div className="gallery">
            {images.slice(0, 5).map((src, i) => <div key={src} className={`gallery-item g${i}`}><SmartImg src={src} alt={`${name} photo ${i + 1}`} /></div>)}
          </div>
        )}

        <div className="details-layout">
          <div className="details-main">
            <section className="panel">
              {(hotel?.starRating || listed?.starRating) > 0 && <Stars n={hotel?.starRating || listed?.starRating} />}
              <h2 className="details-title">{name}</h2>
              {address && <span className="muted small"><Icon name="pin" size={13} /> {fullAddress(address) || placeLine(address)}</span>}
              {hotel?.headline && <p className="details-desc">{hotel.headline}</p>}
              {hotel?.amenities?.length > 0 && <div className="amenities">{hotel.amenities.slice(0, 10).map((a) => <span key={a} className="tag">{a}</span>)}</div>}
            </section>

            <section className="panel" id="rooms">
              <h3>Choose your room</h3>
              <p className="muted small">
                Prices are for {fmtDay(search.checkin)} – {fmtDay(search.checkout)} ({nightsLabel(nights)}), {roomsLabel(search.rooms)}, including taxes and fees.
              </p>
              {!details && !error && <p role="status"><span className="spinner sm" /> Checking live room prices…</p>}
              {error && (
                <div className="api-message error" role="alert">
                  <p>{error}</p>
                  <div className="btn-row">
                    {!expired && <button type="button" className="btn btn-outline btn-sm" onClick={() => setAttempt(attempt + 1)}>Try again</button>}
                    <Link to={resultsPath} className="btn btn-primary btn-sm">Search again</Link>
                  </div>
                </div>
              )}
              {details && groups.length === 0 && <p className="muted">No rooms are available at this hotel for your dates any more. <Link className="link" to={resultsPath}>See other hotels</Link></p>}
              <div className="room-list">
                {groups.map(([key, options]) => {
                  const room = options[0].rooms[0];
                  const roomImage = room.details?.images?.[0];
                  return (
                    <div key={key} className="room-card">
                      <div className="room-info">
                        {roomImage && <div className="room-img"><SmartImg src={roomImage} alt={key} /></div>}
                        <h4>{key}</h4>
                        <RoomFacts details={room.details} />
                      </div>
                      <div className="room-plans">
                        {options.map((option) => (
                          <OptionRow key={option.optionId} option={option} nights={nights}
                            busy={selecting === option.optionId} error={selectError[option.optionId]} onSelect={() => select(option)} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {hotel?.amenities?.length > 0 && (
              <section className="panel">
                <h3>Amenities</h3>
                <div className="amenity-grid">{hotel.amenities.map((a) => <span key={a}><Icon name="check" size={14} /> {a}</span>)}</div>
              </section>
            )}

            {hotel?.descriptions?.map((d) => (
              <section key={d.title} className="panel">
                <h3>{d.title}</h3>
                <p className="details-desc pre-line">{d.text}</p>
              </section>
            ))}

            {hotel && (
              <section className="panel">
                <h3>Hotel policies</h3>
                <ul className="policy-list">
                  {hotel.policies.checkInFrom && <li>Check-in from {hotel.policies.checkInFrom}{hotel.policies.checkInUntil ? ` until ${hotel.policies.checkInUntil}` : ''}.</li>}
                  {hotel.policies.checkOutUntil && <li>Check-out until {hotel.policies.checkOutUntil}.</li>}
                  {hotel.policies.minimumCheckInAge && <li>The lead guest must be at least {hotel.policies.minimumCheckInAge} to check in.</li>}
                  <li>A valid government photo ID is required for every guest at check-in.</li>
                </ul>
                {hotel.policies.notes.map((n) => (
                  <div key={n.title} className="policy-note">
                    <h4>{n.title}</h4>
                    <p className="muted small pre-line">{n.text}</p>
                  </div>
                ))}
              </section>
            )}

            {bbox && (
              <section className="panel">
                <h3>Location</h3>
                <iframe className="map" title="Hotel location" loading="lazy"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`} />
                <p className="muted small"><Icon name="pin" size={13} /> {fullAddress(address)}</p>
              </section>
            )}
          </div>

          <aside className="details-side">
            <div className="panel price-card">
              <span className="muted small">{cheapest ? 'Rooms from' : 'Checking prices…'}</span>
              <b className="price big">{cheapest ? formatPrice(cheapest.price.total) : listed ? formatPrice(listed.price.total) : '—'}</b>
              <span className="muted small">{nightsLabel(nights)}, incl. taxes & fees</span>
              <div className="kv-grid one">
                <div><span className="muted small">Dates</span><b>{fmtDay(search.checkin)} – {fmtDay(search.checkout)}</b></div>
                <div><span className="muted small">Rooms & guests</span><b>{roomsLabel(search.rooms)}</b></div>
                {hotel?.policies?.checkInFrom && <div><span className="muted small">Check-in / check-out</span><b>{hotel.policies.checkInFrom} / {hotel.policies.checkOutUntil || '—'}</b></div>}
              </div>
              {cheapest && <p className="muted small">{freeCancellationText(cheapest.cancellation) || 'The cheapest room is non-refundable.'}</p>}
              <a href="#rooms" className="btn btn-primary block">Select a room</a>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
